/**
 * Enhanced Retry Strategy
 * Provides sophisticated retry mechanisms for failed requests
 */
export var CircuitState;
(function (CircuitState) {
    CircuitState["CLOSED"] = "CLOSED";
    CircuitState["OPEN"] = "OPEN";
    CircuitState["HALF_OPEN"] = "HALF_OPEN";
})(CircuitState || (CircuitState = {}));
/**
 * Retry Strategy Class
 */
export class RetryStrategy {
    constructor(config = {}) {
        this.config = {
            maxAttempts: config.maxAttempts ?? 3,
            initialDelay: config.initialDelay ?? 1000,
            maxDelay: config.maxDelay ?? 30000,
            backoffMultiplier: config.backoffMultiplier ?? 2,
            jitter: config.jitter ?? true,
            retryableStatuses: config.retryableStatuses ?? [408, 429, 500, 502, 503, 504],
            retryableErrors: config.retryableErrors ?? ['ECONNRESET', 'ETIMEDOUT', 'ENOTFOUND'],
            onRetry: config.onRetry,
            shouldRetry: config.shouldRetry
        };
    }
    /**
     * Execute request with retry logic
     */
    async execute(requestFn, context) {
        let lastError;
        for (let attempt = 1; attempt <= this.config.maxAttempts; attempt++) {
            try {
                return await requestFn();
            }
            catch (error) {
                lastError = error;
                if (!this.shouldRetry(error, attempt)) {
                    throw error;
                }
                if (attempt < this.config.maxAttempts) {
                    const delay = this.calculateDelay(attempt);
                    console.log(`⏳ Retry ${attempt}/${this.config.maxAttempts - 1} for ${context?.method} ${context?.url} after ${delay}ms`);
                    if (this.config.onRetry) {
                        this.config.onRetry(attempt, error);
                    }
                    await this.sleep(delay);
                }
            }
        }
        throw lastError;
    }
    /**
     * Check if request should be retried
     */
    shouldRetry(error, attempt) {
        if (attempt >= this.config.maxAttempts) {
            return false;
        }
        // Custom retry logic
        if (this.config.shouldRetry) {
            return this.config.shouldRetry(error, attempt);
        }
        // Check retryable status codes
        if (error.status && this.config.retryableStatuses.includes(error.status)) {
            return true;
        }
        // Check retryable error codes
        if (error.code && this.config.retryableErrors.includes(error.code)) {
            return true;
        }
        // Network errors are retryable
        if (error.name === 'NetworkError' || error.message?.includes('network')) {
            return true;
        }
        return false;
    }
    /**
     * Calculate delay for next retry with exponential backoff and jitter
     */
    calculateDelay(attempt) {
        // Exponential backoff
        let delay = this.config.initialDelay * Math.pow(this.config.backoffMultiplier, attempt - 1);
        // Apply max delay cap
        delay = Math.min(delay, this.config.maxDelay);
        // Add jitter to prevent thundering herd
        if (this.config.jitter) {
            const jitterAmount = delay * 0.2; // 20% jitter
            delay = delay + (Math.random() - 0.5) * jitterAmount;
        }
        return Math.round(delay);
    }
    /**
     * Sleep for specified milliseconds
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    /**
     * Update configuration
     */
    updateConfig(config) {
        this.config = { ...this.config, ...config };
    }
}
/**
 * Circuit Breaker Pattern Implementation
 */
export class CircuitBreaker {
    constructor(config = {}) {
        this.state = CircuitState.CLOSED;
        this.failureCount = 0;
        this.successCount = 0;
        this.lastFailureTime = null;
        this.halfOpenRequests = 0;
        this.config = {
            enabled: config.enabled ?? true,
            failureThreshold: config.failureThreshold ?? 5,
            resetTimeout: config.resetTimeout ?? 60000, // 1 minute
            halfOpenRequests: config.halfOpenRequests ?? 3
        };
    }
    /**
     * Execute request with circuit breaker protection
     */
    async execute(requestFn) {
        if (!this.config.enabled) {
            return requestFn();
        }
        // Check circuit state
        const canExecute = this.canExecute();
        if (!canExecute) {
            throw new Error(`Circuit breaker is OPEN. Service unavailable.`);
        }
        try {
            const result = await requestFn();
            this.onSuccess();
            return result;
        }
        catch (error) {
            this.onFailure();
            throw error;
        }
    }
    /**
     * Check if request can be executed based on circuit state
     */
    canExecute() {
        this.updateState();
        switch (this.state) {
            case CircuitState.CLOSED:
                return true;
            case CircuitState.OPEN:
                return false;
            case CircuitState.HALF_OPEN:
                if (this.halfOpenRequests < this.config.halfOpenRequests) {
                    this.halfOpenRequests++;
                    return true;
                }
                return false;
            default:
                return false;
        }
    }
    /**
     * Update circuit state based on current conditions
     */
    updateState() {
        if (this.state === CircuitState.OPEN && this.shouldAttemptReset()) {
            console.log('🔌 Circuit breaker transitioning to HALF_OPEN');
            this.state = CircuitState.HALF_OPEN;
            this.halfOpenRequests = 0;
            this.successCount = 0;
            this.failureCount = 0;
        }
    }
    /**
     * Check if circuit should attempt reset
     */
    shouldAttemptReset() {
        return (this.lastFailureTime !== null &&
            Date.now() - this.lastFailureTime >= this.config.resetTimeout);
    }
    /**
     * Handle successful request
     */
    onSuccess() {
        this.successCount++;
        if (this.state === CircuitState.HALF_OPEN) {
            if (this.successCount >= this.config.halfOpenRequests) {
                console.log('✅ Circuit breaker transitioning to CLOSED');
                this.state = CircuitState.CLOSED;
                this.failureCount = 0;
                this.successCount = 0;
            }
        }
        else if (this.state === CircuitState.CLOSED) {
            // Reset failure count on success in closed state
            this.failureCount = 0;
        }
    }
    /**
     * Handle failed request
     */
    onFailure() {
        this.failureCount++;
        this.lastFailureTime = Date.now();
        if (this.state === CircuitState.HALF_OPEN) {
            console.log('⚠️  Circuit breaker transitioning back to OPEN');
            this.state = CircuitState.OPEN;
        }
        else if (this.state === CircuitState.CLOSED) {
            if (this.failureCount >= this.config.failureThreshold) {
                console.log('🔴 Circuit breaker transitioning to OPEN');
                this.state = CircuitState.OPEN;
            }
        }
    }
    /**
     * Get circuit breaker status
     */
    getStatus() {
        return {
            state: this.state,
            failureCount: this.failureCount,
            successCount: this.successCount,
            lastFailureTime: this.lastFailureTime
        };
    }
    /**
     * Reset circuit breaker
     */
    reset() {
        this.state = CircuitState.CLOSED;
        this.failureCount = 0;
        this.successCount = 0;
        this.lastFailureTime = null;
        this.halfOpenRequests = 0;
        console.log('🔄 Circuit breaker reset to CLOSED');
    }
    /**
     * Update configuration
     */
    updateConfig(config) {
        this.config = { ...this.config, ...config };
    }
}
/**
 * Retry policies for common scenarios
 */
export const retryPolicies = {
    /**
     * Conservative retry policy
     */
    conservative: () => ({
        maxAttempts: 2,
        initialDelay: 2000,
        maxDelay: 10000,
        backoffMultiplier: 2
    }),
    /**
     * Aggressive retry policy
     */
    aggressive: () => ({
        maxAttempts: 5,
        initialDelay: 500,
        maxDelay: 30000,
        backoffMultiplier: 1.5,
        jitter: true
    }),
    /**
     * No retry policy
     */
    noRetry: () => ({
        maxAttempts: 1
    }),
    /**
     * Rate limit retry policy (for 429 errors)
     */
    rateLimit: () => ({
        maxAttempts: 3,
        initialDelay: 5000,
        maxDelay: 60000,
        backoffMultiplier: 3,
        retryableStatuses: [429],
        shouldRetry: (error) => {
            // Check for Retry-After header
            if (error.response?.headers?.['retry-after']) {
                const retryAfter = parseInt(error.response.headers['retry-after']);
                if (!isNaN(retryAfter)) {
                    return true;
                }
            }
            return error.status === 429;
        }
    })
};
//# sourceMappingURL=retry-strategy.js.map